import { useState } from 'react'
import { Button, Modal, Skeleton, Spinner, TextArea } from '@heroui/react'

import { errorMessage } from '@/lib/api/api-error'

import type { ExceptionResolution, PosException } from '../types'
import { useException } from '../hooks'
import {
  branchLabel,
  chipStyle,
  exceptionStatusStyles,
  money,
  relativeTime,
  resolutionDescriptions,
  resolutionLabels,
} from './pos-format'
import { StatusChip } from './status-chip'

interface ExceptionDetailDialogProps {
  exception: PosException | null
  onOpenChange: (open: boolean) => void
  onResolve: (id: string, resolution: ExceptionResolution, note?: string) => Promise<unknown>
  onDiscard: (id: string, note?: string) => Promise<unknown>
  isSubmitting: boolean
  error?: unknown
  /** Deciding needs `edit` on the POS module; reading does not. */
  canDecide?: boolean
}

/**
 * One exception, what happened, and what the options actually do.
 *
 * The choices come from the row's own `availableResolutions` — the server says
 * which ones it will accept, and this offers exactly those. A button the API
 * would refuse is worse than no button: somebody presses it while a queue is
 * forming, and the queue does not move.
 */
export function ExceptionDetailDialog({
  exception,
  onOpenChange,
  onResolve,
  onDiscard,
  isSubmitting,
  error,
  canDecide = true,
}: ExceptionDetailDialogProps) {
  const [choice, setChoice] = useState<ExceptionResolution | null>(null)
  const [note, setNote] = useState('')
  const { exception: detail, isLoading } = useException(exception?.id ?? null)

  const row = detail ?? exception

  function close() {
    setChoice(null)
    setNote('')
    onOpenChange(false)
  }

  async function submit() {
    if (!row || !choice) return

    await onResolve(row.id, choice, note.trim() || undefined)
    close()
  }

  async function discard() {
    if (!row) return

    await onDiscard(row.id, note.trim() || undefined)
    close()
  }

  return (
    <Modal isOpen={Boolean(exception)} onOpenChange={(open) => (open ? undefined : close())}>
      <Modal.Backdrop className="overlay-scope" variant="blur">
        <Modal.Container size="md" placement="center">
          <Modal.Dialog>
            <Modal.CloseTrigger className="end-3 top-3 size-10" />

            <Modal.Header className="gap-1 pe-10">
              <Modal.Heading>{row?.number}</Modal.Heading>
              <p className="text-muted text-xs">{row?.reasonTitle}</p>
            </Modal.Header>

            <Modal.Body className="flex flex-col gap-5">
              {row && (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusChip style={chipStyle(exceptionStatusStyles, row.status)} />
                    <span className="text-muted text-xs">
                      {branchLabel(row.branch)}
                      {row.registerCode && ` · ${row.registerCode}`} ·{' '}
                      {relativeTime(row.createdAt)}
                    </span>
                  </div>

                  {row.detail && <p className="text-sm leading-relaxed">{row.detail}</p>}

                  <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <Figure label="Uploaded" value={money(row.amount)} />
                    {row.counterpartAmount !== null && (
                      <Figure label="Already on file" value={money(row.counterpartAmount)} />
                    )}
                    {row.orderNumber && <Figure label="Order" value={row.orderNumber} />}
                  </dl>

                  {row.isOpen && canDecide ? (
                    <section className="flex flex-col gap-3">
                      <h3 className="text-sm font-medium">What should happen?</h3>

                      {row.availableResolutions.length === 0 ? (
                        <p className="text-muted text-xs">
                          Nothing to choose — this one was settled by the server.
                        </p>
                      ) : (
                        <div className="flex flex-col gap-2">
                          {row.availableResolutions.map((resolution) => (
                            <label
                              key={resolution}
                              className={`border-border flex cursor-pointer gap-3 rounded-xl border p-3 transition-colors ${
                                choice === resolution ? 'border-foreground bg-surface' : ''
                              }`}
                            >
                              <input
                                type="radio"
                                name="resolution"
                                className="mt-1 shrink-0"
                                checked={choice === resolution}
                                onChange={() => setChoice(resolution)}
                              />
                              <span className="flex flex-col gap-0.5">
                                <span className="text-sm font-medium">
                                  {resolutionLabels[resolution]}
                                </span>
                                {/* The promise this screen makes is that every
                                    option explains what it does. Without this
                                    line "Keep what is already on file" is a
                                    guess about somebody else's money. */}
                                <span className="text-muted text-xs leading-relaxed">
                                  {resolutionDescriptions[resolution]}
                                </span>
                              </span>
                            </label>
                          ))}
                        </div>
                      )}

                      <label className="flex flex-col gap-1.5">
                        <span className="text-muted text-xs">Note (optional)</span>
                        <TextArea
                          value={note}
                          onChange={(event) => setNote(event.target.value)}
                          maxLength={500}
                          className="w-full"
                        />
                      </label>
                    </section>
                  ) : (
                    row.resolution && (
                      <p className="text-muted text-xs">
                        {resolutionLabels[row.resolution]} · {relativeTime(row.resolvedAt)}
                        {row.resolutionNote && (
                          <>
                            {' — '}
                            <span className="text-foreground">{row.resolutionNote}</span>
                          </>
                        )}
                      </p>
                    )
                  )}

                  <section className="flex flex-col gap-2">
                    <h3 className="text-sm font-medium">The upload</h3>
                    {isLoading ? (
                      <Skeleton className="h-24 w-full rounded-xl" />
                    ) : detail?.payload ? (
                      /* Verbatim, because it is the evidence. A decision taken
                         from a summary cannot be re-checked, and these are the
                         rows an auditor asks about. */
                      <pre className="border-border bg-background max-h-48 overflow-auto rounded-xl border p-3 text-xs">
                        {JSON.stringify(detail.payload, null, 2)}
                      </pre>
                    ) : (
                      <p className="text-muted text-xs">
                        The device sent nothing readable — which is often the exception itself.
                      </p>
                    )}
                  </section>

                  {error != null && (
                    <p role="alert" className="text-danger text-xs">
                      {errorMessage(error, 'Could not record that decision.')}
                    </p>
                  )}
                </>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button variant="ghost" onPress={close} isDisabled={isSubmitting}>
                Close
              </Button>

              {row?.isOpen && canDecide && (
                <>
                  {/* Always available, even when the listed resolutions are a
                      choice between two figures neither of which is right. */}
                  <Button
                    variant="ghost"
                    className="border-border rounded-full border"
                    isDisabled={isSubmitting}
                    onPress={() => void discard()}
                  >
                    Discard
                  </Button>

                  <Button
                    variant="primary"
                    isDisabled={isSubmitting || !choice}
                    onPress={() => void submit()}
                  >
                    {isSubmitting && <Spinner size="sm" />}
                    {isSubmitting ? 'Working…' : 'Apply'}
                  </Button>
                </>
              )}
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  )
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-muted text-xs">{label}</dt>
      <dd className="font-medium tabular-nums">{value}</dd>
    </div>
  )
}
